import type { AIFeedbackResponse } from "@memorang/types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

type ResponseData = Record<string, unknown>;
type AIResponseMap = Record<string, AIFeedbackResponse>;

type AIFeedbackState = {
	streaming: boolean;
	userResponse?: ResponseData;
	canvasResponse?: ResponseData;
	audioResponse?: ResponseData;
	aiResponse?: AIResponseMap;
	showError: boolean;
	slideshowDurationInSecondsByItemId: Record<number, number>;
	setStreaming: (streaming: boolean) => void;
	setUserResponse: (itemId: number, data: unknown) => void;
	setCanvasResponse: (itemId: number, data: unknown) => void;
	setAudioResponse: (itemId: number, data: unknown) => void;
	setShowError: (showError: boolean) => void;
	updateTranscription: (itemId: number, data: string) => void;
	resetAiFeedbackStore: () => void;
	updateRunDetails: (
		itemId: number,
		data: AIFeedbackResponse["runDetails"],
	) => void;
	updateSubskillAssessments: (
		itemId: number,
		data: AIFeedbackResponse["subskillAssessments"],
	) => void;
	updateScore: (itemId: number, data: AIFeedbackResponse["score"]) => void;
	updateExplanation: (
		itemId: number,
		data: AIFeedbackResponse["explanation"],
	) => void;
	updateAnswer: (itemId: number, data: AIFeedbackResponse["answer"]) => void;
	incrementRetries: (itemId: number) => void;
	updateSlideshowDurationInSeconds: (itemId: number, duration: number) => void;
};
const useAIFeedbackStore = create<AIFeedbackState>()(
	immer((set) => ({
		streaming: false,
		showError: false,
		slideshowDurationInSecondsByItemId: {},
		setStreaming: (streaming) =>
			set((state) => {
				state.streaming = streaming;
			}),
		setUserResponse: (itemId, data) =>
			set((state) => {
				if (!state.userResponse) {
					state.userResponse = {};
				}
				state.userResponse[itemId] = data;
			}),
		setCanvasResponse: (itemId, data) =>
			set((state) => {
				if (!state.canvasResponse) {
					state.canvasResponse = {};
				}
				state.canvasResponse[itemId] = data;
			}),
		setAudioResponse: (itemId, data) =>
			set((state) => {
				if (!state.audioResponse) {
					state.audioResponse = {};
				}
				state.audioResponse[itemId] = data;
			}),
		setShowError: (showError) =>
			set((state) => {
				state.showError = showError;
			}),
		updateTranscription: (itemId, transcription) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].transcription = transcription;
			}),
		resetAiFeedbackStore: () =>
			set((state) => {
				state.streaming = false;
				state.showError = false;
				state.userResponse = undefined;
				state.canvasResponse = undefined;
				state.audioResponse = undefined;
				state.aiResponse = undefined;
			}),
		updateRunDetails: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].runDetails = data;
			}),
		updateSubskillAssessments: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].subskillAssessments = data;
			}),
		updateScore: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].score = data;
			}),
		updateExplanation: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].explanation = data;
			}),
		updateAnswer: (itemId, data) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].answer = data;
			}),
		incrementRetries: (itemId) =>
			set((state) => {
				state.aiResponse = state.aiResponse || {};
				state.aiResponse[itemId] = state.aiResponse[itemId] || {};
				state.aiResponse[itemId].retries =
					(state.aiResponse[itemId].retries || 0) + 1;
			}),
		updateSlideshowDurationInSeconds: (itemId, duration) =>
			set((state) => {
				state.slideshowDurationInSecondsByItemId[itemId] = duration;
			}),
	})),
);

export default useAIFeedbackStore;
